import { computed, set } from "@ember/object";
import { trustHTML } from "@ember/template";
import { tagName } from "@ember-decorators/component";
import { on } from "@ember-decorators/object";
import RSVP from "rsvp";
import { isTesting } from "discourse/lib/environment";
import loadScript from "discourse/lib/load-script";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";
import AdComponent from "./ad-component";

let _loaded = false,
  _promise = null,
  ads = {},
  nextSlotNum = 1,
  renderCounts = {};

function getNextSlotNum() {
  return nextSlotNum++;
}

function splitWidthInt(value) {
  let str = value.substring(0, 3);
  return str.trim();
}

function splitHeightInt(value) {
  let str = value.substring(4, 7);
  return str.trim();
}

// This creates an array for the values of the custom targeting key
function valueParse(value) {
  let final = value.replace(/ /g, "");
  final = final.replace(/['"]+/g, "");
  final = final.split(",");
  return final;
}

// This creates an array for the key of the custom targeting key
function keyParse(word) {
  let key = word;
  key = key.replace(/['"]+/g, "");
  key = key.split("\n");
  return key;
}

// This builds the targeting map for that location, for slot.setConfig({ targeting })
function custom_targeting(key_array, value_array) {
  const targeting = {};
  for (let i = 0; i < key_array.length; i++) {
    if (key_array[i]) {
      targeting[key_array[i]] = valueParse(value_array[i]);
    }
  }
  return targeting;
}

const DESKTOP_SETTINGS = {
  "above-site-header": {
    code: "dfp_above_site_header_code",
    sizes: "dfp_above_site_header_ad_sizes",
    targeting_keys: "dfp_target_above_site_header_key_code",
    targeting_values: "dfp_target_above_site_header_value_code",
  },
  "topic-list-top": {
    code: "dfp_topic_list_top_code",
    sizes: "dfp_topic_list_top_ad_sizes",
    targeting_keys: "dfp_target_topic_list_top_key_code",
    targeting_values: "dfp_target_topic_list_top_value_code",
  },
  "topic-above-post-stream": {
    code: "dfp_topic_above_post_stream_code",
    sizes: "dfp_topic_above_post_stream_ad_sizes",
    targeting_keys: "dfp_target_topic_above_post_stream_key_code",
    targeting_values: "dfp_target_topic_above_post_stream_value_code",
  },
  "topic-above-suggested": {
    code: "dfp_topic_above_suggested_code",
    sizes: "dfp_topic_above_suggested_ad_sizes",
    targeting_keys: "dfp_target_topic_above_suggested_key_code",
    targeting_values: "dfp_target_topic_above_suggested_value_code",
  },
  "post-bottom": {
    code: "dfp_post_bottom_code",
    sizes: "dfp_post_bottom_ad_sizes",
    targeting_keys: "dfp_target_post_bottom_key_code",
    targeting_values: "dfp_target_post_bottom_value_code",
  },
};

const MOBILE_SETTINGS = {
  "above-site-header": {
    code: "dfp_mobile_above_site_header_code",
    sizes: "dfp_mobile_above_site_header_ad_sizes",
    targeting_keys: "dfp_target_above_site_header_key_code",
    targeting_values: "dfp_target_above_site_header_value_code",
  },
  "topic-list-top": {
    code: "dfp_mobile_topic_list_top_code",
    sizes: "dfp_mobile_topic_list_top_ad_sizes",
    targeting_keys: "dfp_target_topic_list_top_key_code",
    targeting_values: "dfp_target_topic_list_top_value_code",
  },
  "topic-above-post-stream": {
    code: "dfp_mobile_topic_above_post_stream_code",
    sizes: "dfp_mobile_topic_above_post_stream_ad_sizes",
    targeting_keys: "dfp_target_topic_above_post_stream_key_code",
    targeting_values: "dfp_target_topic_above_post_stream_value_code",
  },
  "topic-above-suggested": {
    code: "dfp_mobile_topic_above_suggested_code",
    sizes: "dfp_mobile_topic_above_suggested_ad_sizes",
    targeting_keys: "dfp_target_topic_above_suggested_key_code",
    targeting_values: "dfp_target_topic_above_suggested_value_code",
  },
  "post-bottom": {
    code: "dfp_mobile_post_bottom_code",
    sizes: "dfp_mobile_post_bottom_ad_sizes",
    targeting_keys: "dfp_target_post_bottom_key_code",
    targeting_values: "dfp_target_post_bottom_value_code",
  },
};

function getWidthAndHeight(placement, settings, isMobile) {
  let config, size;

  if (isMobile) {
    config = MOBILE_SETTINGS[placement];
  } else {
    config = DESKTOP_SETTINGS[placement];
  }

  if (!renderCounts[placement]) {
    renderCounts[placement] = 0;
  }

  const sizes = (settings[config.sizes] || "").split("|");

  if (sizes.length === 1) {
    size = sizes[0];
  } else {
    size = sizes[renderCounts[placement] % sizes.length];
    renderCounts[placement] += 1;
  }

  if (size === "fluid") {
    return { width: "fluid", height: "fluid" };
  }

  const sizeObj = {
    width: parseInt(splitWidthInt(size), 10),
    height: parseInt(splitHeightInt(size), 10),
  };

  if (!isNaN(sizeObj.width) && !isNaN(sizeObj.height)) {
    return sizeObj;
  }
}

function defineSlot(
  divId,
  placement,
  settings,
  isMobile,
  width,
  height,
  categoryTarget
) {
  if (!settings.dfp_publisher_id) {
    return;
  }

  if (ads[divId]) {
    return ads[divId];
  }

  let ad, config, publisherId;

  if (isMobile) {
    publisherId = settings.dfp_publisher_id_mobile || settings.dfp_publisher_id;
    config = MOBILE_SETTINGS[placement];
  } else {
    publisherId = settings.dfp_publisher_id;
    config = DESKTOP_SETTINGS[placement];
  }

  ad = window.googletag.defineSlot(
    "/" + publisherId + "/" + settings[config.code],
    [width, height],
    divId
  );

  const targeting = custom_targeting(
    keyParse(settings[config.targeting_keys]),
    keyParse(settings[config.targeting_values])
  );

  if (categoryTarget) {
    targeting["discourse-category"] = categoryTarget;
  }

  ad.setConfig({ targeting });

  ad.addService(window.googletag.pubads());

  ads[divId] = { ad, width, height };
  return ads[divId];
}

function destroySlot(divId) {
  if (ads[divId] && window.googletag) {
    window.googletag.destroySlots([ads[divId].ad]);
    delete ads[divId];
  }
}

function loadGoogle() {
  /**
   * Refer to this article for help:
   * https://support.google.com/admanager/answer/4578089?hl=en
   */

  if (_loaded) {
    return RSVP.resolve();
  }

  if (_promise) {
    return _promise;
  }

  // The boilerplate code
  let dfpSrc =
    ("https:" === document.location.protocol ? "https:" : "http:") +
    "//securepubads.g.doubleclick.net/tag/js/gpt.js";
  _promise = loadScript(dfpSrc, { scriptTag: true }).then(function () {
    _loaded = true;
    if (window.googletag === undefined) {
      // eslint-disable-next-line no-console
      console.log("googletag is undefined!");
    }

    window.googletag.cmd.push(function () {
      window.googletag.setConfig({
        // Infinite scroll requires SRA:
        singleRequest: true,

        // we always use refresh() to fetch the ads:
        disableInitialLoad: true,

        // Improve CSP compatibility (https://developers.google.com/publisher-tag/guides/content-security-policy)
        safeFrame: { forceSafeFrame: true },
      });

      window.googletag.enableServices();
    });
  });

  window.googletag = window.googletag || { cmd: [] };

  return _promise;
}

@tagName("")
export default class GoogleDfpAd extends AdComponent {
  loadedGoogletag = false;
  lastAdRefresh = null;

  @computed("size.width")
  get width() {
    return this.size?.width;
  }

  set width(value) {
    set(this, "size.width", value);
  }

  @computed("size.height")
  get height() {
    return this.size?.height;
  }

  set height(value) {
    set(this, "size.height", value);
  }

  @computed
  get size() {
    return getWidthAndHeight(
      this.get("placement"),
      this.siteSettings,
      this.site.mobileView
    );
  }

  @computed(
    "siteSettings.dfp_publisher_id",
    "siteSettings.dfp_publisher_id_mobile",
    "site.mobileView"
  )
  get publisherId() {
    if (this.site?.mobileView) {
      return (
        this.siteSettings?.dfp_publisher_id_mobile ||
        this.siteSettings?.dfp_publisher_id
      );
    } else {
      return this.siteSettings?.dfp_publisher_id;
    }
  }

  @computed("placement", "postNumber")
  get divId() {
    let slotNum = getNextSlotNum();
    if (this.postNumber) {
      return `div-gpt-ad-${slotNum}-${this.placement}-${this.postNumber}`;
    } else {
      return `div-gpt-ad-${slotNum}-${this.placement}`;
    }
  }

  @computed("placement", "showAd")
  get adUnitClass() {
    return this.showAd ? `dfp-ad-${this.placement}` : "";
  }

  @computed("width", "height")
  get adWrapperStyle() {
    if (this.width !== "fluid") {
      return trustHTML(`width: ${this.width}px; height: ${this.height}px;`);
    }
  }

  @computed("width")
  get adTitleStyleMobile() {
    if (this.width !== "fluid") {
      return trustHTML(`width: ${this.width}px;`);
    }
  }

  @computed(
    "publisherId",
    "showDfpAds",
    "showToGroups",
    "showAfterPost",
    "showOnCurrentPage",
    "size"
  )
  get showAd() {
    return (
      this.publisherId &&
      this.showDfpAds &&
      this.showToGroups &&
      this.showAfterPost &&
      this.showOnCurrentPage &&
      this.size
    );
  }

  @computed
  get showDfpAds() {
    if (!this.currentUser) {
      return true;
    }

    return this.currentUser.show_dfp_ads;
  }

  @computed("postNumber")
  get showAfterPost() {
    if (!this.postNumber) {
      return true;
    }

    return this.isNthPost(parseInt(this.siteSettings.dfp_nth_post_code, 10));
  }

  // 3 second delay between calls to refresh ads in a component.
  // Ember often calls updated() more than once, and *sometimes*
  // updated() is called after _initGoogleDFP().
  shouldRefreshAd() {
    const lastAdRefresh = this.get("lastAdRefresh");
    if (!lastAdRefresh) {
      return true;
    }
    return new Date() - lastAdRefresh > 3000;
  }

  @on("didUpdate")
  updated() {
    if (!this.shouldRefreshAd()) {
      return;
    }

    let slot = ads[this.get("divId")];
    if (!(slot && slot.ad)) {
      return;
    }

    let ad = slot.ad,
      categorySlug = this.get("currentCategorySlug");

    if (this.get("loadedGoogletag")) {
      this.set("lastAdRefresh", new Date());
      window.googletag.cmd.push(() => {
        ad.setConfig({
          targeting: { "discourse-category": categorySlug || "0" },
        });
        window.googletag.pubads().refresh([ad]);
      });
    }
  }

  buildImpressionPayload() {
    return {
      ad_plugin_impression: {
        ad_type: this.site.ad_types.dfp,
        ad_plugin_house_ad_id: null,
        placement: this.placement,
      },
    };
  }

  willRender() {
    super.willRender(...arguments);

    if (!this.get("showAd")) {
      return;
    }
  }

  @on("willDestroyElement")
  cleanup() {
    destroySlot(this.get("divId"));
  }

  @on("didInsertElement")
  _initGoogleDFP() {
    if (isTesting()) {
      return; // Don't load external JS during tests
    }

    if (!this.get("showAd")) {
      return;
    }

    loadGoogle().then(() => {
      this.set("loadedGoogletag", true);
      this.set("lastAdRefresh", new Date());

      window.googletag.cmd.push(() => {
        let slot = defineSlot(
          this.get("divId"),
          this.get("placement"),
          this.siteSettings,
          this.site.mobileView,
          this.get("width"),
          this.get("height"),
          this.get("currentCategorySlug") || "0"
        );
        if (slot && slot.ad) {
          // Display has to be called before refresh
          // and after the slot div is in the page.
          window.googletag.display(this.get("divId"));
          window.googletag.pubads().refresh([slot.ad]);
        }
      });
    });
  }

  <template>
    <div class={{dConcatClass "google-dfp-ad" this.adUnitClass}} ...attributes>
      {{#if this.showAd}}
        {{#if this.site.mobileView}}
          <div class="google-dfp-ad-label" style={{this.adTitleStyleMobile}}><h2
            >{{i18n "adplugin.advertisement_label"}}</h2></div>
          <div
            align="center"
            class="dfp-ad-unit"
            id={{this.divId}}
            style={{this.adWrapperStyle}}
          ></div>
        {{else}}
          <div class="google-dfp-ad-label"><h2>{{i18n
                "adplugin.advertisement_label"
              }}</h2></div>
          <div
            align="center"
            class="dfp-ad-unit"
            id={{this.divId}}
            style={{this.adWrapperStyle}}
          ></div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
